"use client";

import { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  Send,
  CheckCircle2,
  Compass,
  Briefcase,
  Loader2,
  AlertTriangle,
  FileText,
  Check,
  Layers,
  Activity,
} from "lucide-react";

import { publicService } from "@/modules/public/home/services/publicService";
import { API_URL } from "@/lib/constants";

export default function IsraelAgritechForm() {
  // STAGE 1: Self-assessment eligibility fields
  const [eligibility, setEligibility] = useState({
    cropSuitability: "",
    landSize: "",
    landContiguity: "", // "yes" | "no"
    investmentReadiness: "", // "yes" | "no" | "partial"
    forwardThinking: "", // "yes" | "no"
    commitment: "", // "yes" | "no"
  });

  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);

  // STAGE 2: Detailed Expression of Interest (EoI) Form fields matching the user screenshot
  const [eoiData, setEoiData] = useState({
    farmerName: "",
    fatherName: "",
    address: "",
    mobile: "",
    altMobile: "",
    emailId: "",
    landSize: "",
    landContiguityDetails: "",
    landOwnership: "", // "Self" | "Leased" | "Partnership" | etc
    googleMapsLocation: "",
    soilTestReport: "", // "yes" | "no"
    waterSource: "", // "Borewell" | "Open Well" | etc
    cropsActivity: [] as string[],
    detailsProjectActivity: "",
    investmentReadiness: "", // "yes" | "no" | "partial"
    fundingPlan: [] as string[],
    additionalNotes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const [rules, setRules] = useState({
    minLandSize: 1,
    landContiguity: "yes",
    investmentReadiness: "partial",
    forwardThinking: "yes",
    commitment: "yes",
  });

  useEffect(() => {
    fetch(`${API_URL}/israel-agritech/settings/rules`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load settings");
        return res.json();
      })
      .then((data) => {
        if (data) {
          setRules({
            minLandSize:
              typeof data.minLandSize === "number" ? data.minLandSize : 1,
            landContiguity: data.landContiguity ?? "yes",
            investmentReadiness: data.investmentReadiness ?? "partial",
            forwardThinking: data.forwardThinking ?? "yes",
            commitment: data.commitment ?? "yes",
          });
        }
      })
      .catch((err) => {
        console.error(
          "Error loading eligibility settings, using defaults:",
          err,
        );
      });
  }, []);

  const handleEligibilityChange = (name: string, value: string) => {
    setEligibility((prev) => ({ ...prev, [name]: value }));
  };

  const handleEoiChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setEoiData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (
    field: "cropsActivity" | "fundingPlan",
    value: string,
  ) => {
    setEoiData((prev) => {
      const list = prev[field];
      if (list.includes(value)) {
        return { ...prev, [field]: list.filter((item) => item !== value) };
      } else {
        return { ...prev, [field]: [...list, value] };
      }
    });
  };

  const checkMyEligibility = (e: React.FormEvent) => {
    e.preventDefault();

    // Quick validation of eligibility inputs
    if (
      !eligibility.cropSuitability ||
      !eligibility.landSize ||
      !eligibility.landContiguity ||
      !eligibility.investmentReadiness ||
      !eligibility.forwardThinking ||
      !eligibility.commitment
    ) {
      alert(
        "Please answer all 6 self-assessment questions before checking eligibility.",
      );
      return;
    }

    setCheckingEligibility(true);

    // Simulate smart assessment calculation delay
    setTimeout(() => {
      const size = parseFloat(eligibility.landSize);
      const eligible =
        eligibility.cropSuitability.trim().length > 0 &&
        !isNaN(size) &&
        size >= rules.minLandSize &&
        (rules.landContiguity === "any" ||
          eligibility.landContiguity === "yes") &&
        (rules.investmentReadiness === "any" ||
          (rules.investmentReadiness === "partial" &&
            (eligibility.investmentReadiness === "yes" ||
              eligibility.investmentReadiness === "partial")) ||
          (rules.investmentReadiness === "yes" &&
            eligibility.investmentReadiness === "yes")) &&
        (rules.forwardThinking === "any" ||
          eligibility.forwardThinking === "yes") &&
        (rules.commitment === "any" || eligibility.commitment === "yes");

      setIsEligible(eligible);
      setEligibilityChecked(true);
      setCheckingEligibility(false);

      // Auto-prefill stage 2 parameters to match Stage 1 inputs (saves time for the farmer!)
      setEoiData((prev) => ({
        ...prev,
        landSize: eligibility.landSize,
        investmentReadiness: eligibility.investmentReadiness,
      }));
    }, 1200);
  };

  const handleSubmitEoi = async (e: React.FormEvent) => {
    e.preventDefault();

    // Quick verification
    if (!eoiData.farmerName || !eoiData.mobile || !eoiData.landSize) {
      alert("Please fill out all required fields marked with *");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        farmerName: eoiData.farmerName,
        fatherName: eoiData.fatherName || null,
        address: eoiData.address || null,
        mobile: eoiData.mobile,
        altMobile: eoiData.altMobile || null,
        emailId: eoiData.emailId || null,
        landSize: parseFloat(eoiData.landSize),
        landContiguityDetails: eoiData.landContiguityDetails || null,
        landOwnership: eoiData.landOwnership || null,
        googleMapsLocation: eoiData.googleMapsLocation || null,
        soilTestReport: eoiData.soilTestReport || "no",
        waterSource: eoiData.waterSource || null,
        cropsActivity: eoiData.cropsActivity,
        detailsProjectActivity: eoiData.detailsProjectActivity || null,
        investmentReadiness: eoiData.investmentReadiness || "no",
        fundingPlan: eoiData.fundingPlan,
        additionalNotes: eoiData.additionalNotes || null,

        // Stage 1 answers
        stage1CropSuitability: eligibility.cropSuitability || null,
        stage1LandContiguity: eligibility.landContiguity === "yes",
        stage1ForwardThinking: eligibility.forwardThinking === "yes",
        stage1Commitment: eligibility.commitment === "yes",
      };

      await publicService.submitIsraelAgritechEoi(payload);

      setSubmitStatus("success");
      // Clear all forms
      setEligibility({
        cropSuitability: "",
        landSize: "",
        landContiguity: "",
        investmentReadiness: "",
        forwardThinking: "",
        commitment: "",
      });
      setEoiData({
        farmerName: "",
        fatherName: "",
        address: "",
        mobile: "",
        altMobile: "",
        emailId: "",
        landSize: "",
        landContiguityDetails: "",
        landOwnership: "",
        googleMapsLocation: "",
        soilTestReport: "",
        waterSource: "",
        cropsActivity: [],
        detailsProjectActivity: "",
        investmentReadiness: "",
        fundingPlan: [],
        additionalNotes: "",
      });
      setEligibilityChecked(false);
    } catch (err: any) {
      alert(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <div className="bg-white rounded-xl shadow-xl border border-green-100 p-8 md:p-12 text-center max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[400px] animate-fade-in">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6 border border-green-200">
          <CheckCircle2 className="w-8 h-8 text-green-600 animate-bounce" />
        </div>
        <h3 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-wide">
          EoI Submitted Successfully!
        </h3>
        <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-md mx-auto mb-8 font-medium">
          Thank you for completing your detailed Expression of Interest (EoI).
          Our Indo-Israel Agritech cooperation team will review your farm
          parameters and contact you within 24-48 hours.
        </p>
        <button
          onClick={() => setSubmitStatus("idle")}
          className="px-8 py-3 bg-[#0f9648] hover:bg-green-700 text-white rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 cursor-pointer"
        >
          Submit Another EOI
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 w-full max-w-4xl mx-auto">
      {/* 1. Farm Eligibility Self-Assessment Form (ON TOP) */}
      <section className="bg-[#f0faf4] rounded-sm p-5 md:p-8 border border-emerald-100/80 shadow-sm flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
            Farm Eligibility - Self Assessment
          </h3>
          <p className="text-xs text-gray-500 bg-yellow-400 p-2 rounded leading-relaxed font-medium">
            Only eligible farmers should proceed to form submission.
          </p>
        </div>

        <form onSubmit={checkMyEligibility} className="flex flex-col gap-4">
          {/* 1. Crop Suitability */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-2.5">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              1. Crop Suitability - Is your crop suitable for Israel technology?
            </label>
            <input
              type="text"
              required
              value={eligibility.cropSuitability}
              onChange={(e) =>
                handleEligibilityChange("cropSuitability", e.target.value)
              }
              placeholder="Enter your crop(s)"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
            />
          </div>

          {/* 2. Land Size */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-2.5">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              2. Land Size - Minimum required area available? (in acres)
            </label>
            <input
              type="number"
              step="any"
              required
              value={eligibility.landSize}
              onChange={(e) =>
                handleEligibilityChange("landSize", e.target.value)
              }
              placeholder="Enter land size in acres"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
            />
          </div>

          {/* 3. Land Contiguity */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-3">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              3. Land Contiguity - Is your land continuous/connected?
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="landContiguity"
                  required
                  checked={eligibility.landContiguity === "yes"}
                  onChange={() =>
                    handleEligibilityChange("landContiguity", "yes")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                Yes
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="landContiguity"
                  required
                  checked={eligibility.landContiguity === "no"}
                  onChange={() =>
                    handleEligibilityChange("landContiguity", "no")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                No
              </label>
            </div>
          </div>

          {/* 4. Investment Readiness */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-3">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              4. Investment Readiness - Are you ready for full/partial
              investment?
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="investmentReadiness"
                  required
                  checked={eligibility.investmentReadiness === "yes"}
                  onChange={() =>
                    handleEligibilityChange("investmentReadiness", "yes")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                Yes
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="investmentReadiness"
                  required
                  checked={eligibility.investmentReadiness === "no"}
                  onChange={() =>
                    handleEligibilityChange("investmentReadiness", "no")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                No
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="investmentReadiness"
                  required
                  checked={eligibility.investmentReadiness === "partial"}
                  onChange={() =>
                    handleEligibilityChange("investmentReadiness", "partial")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                Partial
              </label>
            </div>
          </div>

          {/* 5. Forward-thinking Approach */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-3">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              5. Forward-thinking approach - Willingness to adopt modern
              methods?
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="forwardThinking"
                  required
                  checked={eligibility.forwardThinking === "yes"}
                  onChange={() =>
                    handleEligibilityChange("forwardThinking", "yes")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                Yes
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="forwardThinking"
                  required
                  checked={eligibility.forwardThinking === "no"}
                  onChange={() =>
                    handleEligibilityChange("forwardThinking", "no")
                  }
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                No
              </label>
            </div>
          </div>

          {/* 6. Commitment to work */}
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100/60 flex flex-col gap-3">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              6. Commitment to work with project team
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="commitment"
                  required
                  checked={eligibility.commitment === "yes"}
                  onChange={() => handleEligibilityChange("commitment", "yes")}
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                Yes
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="radio"
                  name="commitment"
                  required
                  checked={eligibility.commitment === "no"}
                  onChange={() => handleEligibilityChange("commitment", "no")}
                  className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                />
                No
              </label>
            </div>
          </div>

          {/* Assess Button */}
          <button
            type="submit"
            disabled={checkingEligibility}
            className="w-full mt-2 bg-[#0f9648] hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg text-sm transition-all duration-200 active:scale-95 shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {checkingEligibility ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing farm suitability...
              </>
            ) : (
              "Check My Eligibility"
            )}
          </button>
        </form>
      </section>

      {/* Results Display Area right below Form 1 */}
      {eligibilityChecked && (
        <div
          id="eligibility-result-banner"
          className="flex flex-col gap-6 animate-fade-in"
        >
          {isEligible ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 md:p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 border border-emerald-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="font-bold text-emerald-800 text-sm md:text-base">
                  Eligible Profile!
                </h4>
                <p className="text-xs text-emerald-600 leading-relaxed font-medium">
                  Based on your self-assessment parameters, your farm satisfies
                  the core technical criteria. Please complete the Expression of
                  Interest (EoI) form directly below.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 md:p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="font-bold text-amber-800 text-sm md:text-base">
                  Consultation Recommended.
                </h4>
                <p className="text-xs text-amber-600 leading-relaxed font-medium">
                  Your parameters do not fully meet standard criteria. However,
                  you can still proceed and complete the Expression of Interest
                  (EoI) form directly below.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 2. Expression of Interest (EoI) Form (ON THE BOTTOM / BELOW) */}
      <section className="bg-white border border-emerald-500/20 rounded-sm p-5 md:p-8 flex flex-col gap-6">
        {/* Form Title */}
        <div className="flex items-center gap-2.5 pb-4 border-b border-gray-100">
          <FileText className="w-6 h-6 text-[#0f9648] shrink-0" />
          <h3 className="text-lg md:text-xl font-bold text-gray-900">
            Expression of Interest (EoI) Form
          </h3>
        </div>

        <form onSubmit={handleSubmitEoi} className="flex flex-col gap-6">
          {/* 1. Basic Details */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-[#0f9648] border-b border-emerald-500/10 pb-1 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              Basic Details
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Name of Farmer cum Investor{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="farmerName"
                  required
                  value={eoiData.farmerName}
                  onChange={handleEoiChange}
                  placeholder="Enter full name"
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Father's / Guardian's Name
                </label>
                <input
                  type="text"
                  name="fatherName"
                  value={eoiData.fatherName}
                  onChange={handleEoiChange}
                  placeholder="Enter guardian name"
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-gray-600">
                Full Address (Village, Post, Taluk, District, PIN)
              </label>
              <textarea
                name="address"
                value={eoiData.address}
                onChange={handleEoiChange}
                placeholder="Enter full address detail"
                className="w-full min-h-[90px] px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  required
                  value={eoiData.mobile}
                  onChange={handleEoiChange}
                  placeholder="Enter primary mobile number"
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Alternate Mobile Number
                </label>
                <input
                  type="tel"
                  name="altMobile"
                  value={eoiData.altMobile}
                  onChange={handleEoiChange}
                  placeholder="Enter alternate phone number"
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-gray-600">
                Email ID
              </label>
              <input
                type="email"
                name="emailId"
                value={eoiData.emailId}
                onChange={handleEoiChange}
                placeholder="e.g. email@example.com"
                className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
              />
            </div>
          </div>

          {/* 2. Farm Area Willing to Bring under Israel Agritech */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-[#0f9648] border-b border-emerald-500/10 pb-1 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              Farm Area Willing to Bring under Israel Agritech
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Land Size (in acres) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  name="landSize"
                  required
                  value={eoiData.landSize}
                  onChange={handleEoiChange}
                  placeholder="Enter land size in acres"
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Land Contiguity Details
                </label>
                <input
                  type="text"
                  name="landContiguityDetails"
                  value={eoiData.landContiguityDetails}
                  onChange={handleEoiChange}
                  placeholder="Specify if continuous or separate plots"
                  className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Land Ownership Type
                </label>
                <div className="relative">
                  <select
                    name="landOwnership"
                    value={eoiData.landOwnership}
                    onChange={handleEoiChange}
                    className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium appearance-none cursor-pointer"
                  >
                    <option value="">Select</option>
                    <option value="Self">Self</option>
                    <option value="Leased">Leased</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Ancestral">Ancestral</option>
                    <option value="Others">Others</option>
                  </select>
                  <div className="absolute right-3.5 top-1/2 -trangray-y-1/2 pointer-events-none w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-gray-500" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Location (Upload Farm Location Via Google Maps)
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="googleMapsLocation"
                    value={eoiData.googleMapsLocation}
                    onChange={handleEoiChange}
                    placeholder="Paste Google Maps link here"
                    className="w-full pl-9 pr-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-bold text-gray-600">
                  Soil Test Report Available?
                </label>
                <div className="flex items-center gap-6 mt-1">
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="soilTestReport"
                      checked={eoiData.soilTestReport === "yes"}
                      onChange={() =>
                        setEoiData((prev) => ({
                          ...prev,
                          soilTestReport: "yes",
                        }))
                      }
                      className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="soilTestReport"
                      checked={eoiData.soilTestReport === "no"}
                      onChange={() =>
                        setEoiData((prev) => ({
                          ...prev,
                          soilTestReport: "no",
                        }))
                      }
                      className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                    />
                    No
                  </label>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-gray-600">
                  Water Source
                </label>
                <div className="relative">
                  <select
                    name="waterSource"
                    value={eoiData.waterSource}
                    onChange={handleEoiChange}
                    className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium appearance-none cursor-pointer"
                  >
                    <option value="">Select Water Source</option>
                    <option value="Borewell">Borewell</option>
                    <option value="Open Well">Open Well</option>
                    <option value="River">River</option>
                    <option value="Canal">Canal</option>
                    <option value="Rainfed">Rainfed</option>
                    <option value="Pond / Tank">Pond / Tank</option>
                    <option value="Others">Others</option>
                  </select>
                  <div className="absolute right-3.5 top-1/2 -trangray-y-1/2 pointer-events-none w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-gray-500" />
                </div>
              </div>
            </div>
          </div>

          {/* 3. Project Participation */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-[#0f9648] border-b border-emerald-500/10 pb-1 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" />
              Project Participation
            </h4>

            {/* Checkboxes for Crop/Activity Planning to take up */}
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-bold text-gray-600">
                Crop(s) and Activity Planning to Take up
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  "Open cultivation",
                  "Greenhouse",
                  "Multiple Crops",
                  "Irrigation",
                  "Poultry",
                  "Others",
                ].map((item) => (
                  <label
                    key={item}
                    className="flex items-center gap-2.5 px-3.5 py-3 bg-emerald-50/20 border border-emerald-100/50 rounded-lg text-xs font-semibold text-gray-700 cursor-pointer select-none hover:bg-emerald-50/50 transition-colors animate-fade-in"
                  >
                    <input
                      type="checkbox"
                      checked={eoiData.cropsActivity.includes(item)}
                      onChange={() =>
                        handleCheckboxChange("cropsActivity", item)
                      }
                      className="w-4 h-4 rounded text-[#0f9648] focus:ring-green-500 border-gray-300"
                    />
                    {item}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-gray-600">
                Details of Project Activity Interested
              </label>
              <textarea
                name="detailsProjectActivity"
                value={eoiData.detailsProjectActivity}
                onChange={handleEoiChange}
                placeholder="Specify project interested to take up including whether dry land or matching"
                className="w-full min-h-[90px] px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium resize-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-bold text-gray-600">
                Investment Readiness
              </label>
              <div className="flex items-center gap-6 mt-1">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="eoiInvestmentReadiness"
                    checked={eoiData.investmentReadiness === "yes"}
                    onChange={() =>
                      setEoiData((prev) => ({
                        ...prev,
                        investmentReadiness: "yes",
                      }))
                    }
                    className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="eoiInvestmentReadiness"
                    checked={eoiData.investmentReadiness === "no"}
                    onChange={() =>
                      setEoiData((prev) => ({
                        ...prev,
                        investmentReadiness: "no",
                      }))
                    }
                    className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                  />
                  No
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="eoiInvestmentReadiness"
                    checked={eoiData.investmentReadiness === "partial"}
                    onChange={() =>
                      setEoiData((prev) => ({
                        ...prev,
                        investmentReadiness: "partial",
                      }))
                    }
                    className="w-4 h-4 text-[#0f9648] focus:ring-green-500 border-gray-300"
                  />
                  Partial
                </label>
              </div>
            </div>

            {/* Checkboxes for Project Funding Plan */}
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-bold text-gray-600">
                Project Funding Plan
              </label>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
                {[
                  "Own Funding",
                  "Local Bank Loan",
                  "Israel Funding Support",
                  "Subsidy incl. CSR",
                  "Others",
                ].map((item) => (
                  <label
                    key={item}
                    className="flex items-center gap-2.5 px-3.5 py-3 bg-emerald-50/20 border border-emerald-100/50 rounded-lg text-xs font-semibold text-gray-700 cursor-pointer select-none hover:bg-emerald-50/50 transition-colors animate-fade-in"
                  >
                    <input
                      type="checkbox"
                      checked={eoiData.fundingPlan.includes(item)}
                      onChange={() => handleCheckboxChange("fundingPlan", item)}
                      className="w-4 h-4 rounded text-[#0f9648] focus:ring-green-500 border-gray-300"
                    />
                    {item}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-gray-600">
                Any Additional Notes / Expectations (Optional)
              </label>
              <textarea
                name="additionalNotes"
                value={eoiData.additionalNotes}
                onChange={handleEoiChange}
                className="w-full min-h-[90px] px-3.5 py-2.5 bg-gray-50/50 border border-gray-200 text-gray-800 text-sm rounded-lg outline-none focus:bg-white focus:border-green-600 focus:ring-2 focus:ring-green-500/10 transition-all font-medium resize-none"
              />
            </div>
          </div>

          {/* Submit EoI Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#0f9648] hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-lg text-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-md hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting EOI...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Submit My EoI
              </>
            )}
          </button>
        </form>
      </section>
    </div>
  );
}
